import styled from 'styled-components';
import { FONTSIZE, sizes, theme } from 'theme';
import strings from '../../../strings/strings.json';
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import useGetRequest from 'hooks/useGetRequest';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import React from 'react';
import SuccessNotifier from 'components/notifiers/SuccessNotifier';
import { BodyRegular } from 'components/Elements';
import { AuthContext } from 'contextProviders/AuthContext';
import Spinner from 'components/Spinner';
import Modal from 'components/modals/Modal';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import Button from 'components/buttons/Button';

type PaymentCardSetupModalProps = {
    closeModal: () => void;
    closeModalAfterAddCard: () => void;
};

export default function PaymentSetupModal({
    closeModal,
    closeModalAfterAddCard,
}: PaymentCardSetupModalProps): JSX.Element {
    const authContext = React.useContext(AuthContext);
    const name = `${authContext.userData.firstName} ${authContext.userData.firstName}`;
    const email = authContext.userData.email;
    const cardAlreadySetup = authContext.userData.organisation!.hasValidPaymentMethod;
    const [cardAdded, setCardAdded] = React.useState<boolean>(false);

    React.useEffect(() => {
        return () => {
            if (cardAdded) {
                closeModalAfterAddCard();
            }
        };
    }, [cardAdded]); //eslint-disable-line

    return (
        <Modal
            onClickCloseButton={closeModal}
            headlineText={strings.paymentSetupModal.headline}
            modalBody={
                <Body>
                    {cardAlreadySetup ? (
                        <BodyRegular>{strings.paymentSetupModal.cardAlreadySetup}</BodyRegular>
                    ) : (
                        <CardSetupFormContent setCardAdded={() => setCardAdded(true)} name={name} email={email} />
                    )}
                </Body>
            }
        />
    );
}

const CardSetupFormContent: React.FC<{ setCardAdded: () => void; name: string; email: string }> = ({
    setCardAdded,
    name,
    email,
}) => {
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

    const { data: cardSetupSecret, loading, called, errors } = useGetRequest<string>(`billing/setup`);

    if (!called || (called && loading)) {
        return <Spinner />;
    }
    if (errors.length || !cardSetupSecret) {
        return (
            <ErrorNotifier
                title={strings.paymentSetupModal.error.title}
                description={strings.paymentSetupModal.error.description}
            />
        );
    }
    return (
        <Elements stripe={stripePromise}>
            <SetupCardElement clientSecret={cardSetupSecret} setCardAdded={setCardAdded} name={name} email={email} />
        </Elements>
    );
};

const SetupCardElement: React.FC<{
    clientSecret: string;
    setCardAdded: () => void;
    name: string;
    email: string;
}> = ({ clientSecret, setCardAdded, name, email }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = React.useState<string>('');
    const [success, setSuccess] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);
        setLoading(true);

        if (!stripe || !elements) {
            return;
        }
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            return;
        }

        const cardSetup = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                card: cardElement,
                billing_details: {
                    name: name,
                    email: email,
                },
            },
        });
        if (cardSetup.error?.message) {
            setError(cardSetup.error.message);
        } else {
            setSuccess(true);
            setCardAdded();
        }

        setLoading(false);
    };

    const cardStyle = {
        style: {
            base: {
                color: theme.textColour,
                fontSize: FONTSIZE.BodyLarge,
                '::placeholder': {
                    color: theme.textColour,
                },
            },
            invalid: {
                color: theme.errorColour,
                iconColor: theme.errorColour,
            },
        },
        hidePostalCode: true,
    };

    return success ? (
        <SuccessNotifier
            title={strings.paymentSetupModal.success.title}
            description={strings.paymentSetupModal.success.description}
        />
    ) : (
        <form onSubmit={handleSubmit}>
            <FormContent>
                <CardElementWrapper>
                    <CardElement options={cardStyle} />
                </CardElementWrapper>
                <ButtonRow>
                    {error && <ErrorNotifierSlim description={error} />}
                    <Button
                        title={strings.paymentSetupModal.saveCardButton.title}
                        aria={strings.paymentSetupModal.saveCardButton.aria}
                        type="submit"
                        submitting={loading}
                    />
                </ButtonRow>
            </FormContent>
        </form>
    );
};

const ButtonRow = styled.div`
    display: flex;
    justify-content: end;
    gap: ${sizes.spacingRg};
    align-items: center;
    height: 80px;
`;

const FormContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    height: 120px;
`;

const CardElementWrapper = styled.div`
    width: 350px;
    height: 20px; !important
`;
